import React, { memo, RefObject, useMemo } from 'react'
import uuid from 'uuid/v4'
import { ISelectBaseProps, SelectBase } from './SelectBase'
import { SelectOption, ISelectOption } from './SelectOption'
import { SelectOptionRenderType } from './recipe'

interface IDisplayOptionLabelProps {
  label?: string | number
  placeholder: string
}

export const DisplayOptionLabel = memo<IDisplayOptionLabelProps>(({ label, placeholder }) => (
  <span className={`ellipsis ${label ? '' : 'text-secondary'}`}>{label ?? placeholder}</span>
))

export interface ISelectProps {
  inputRef?: RefObject<HTMLInputElement>
  id?: string
  label?: ISelectBaseProps['label']
  placeholder?: string
  dataTest?: string
  options: ISelectOption[]
  value?: string | number | ISelectOption
  onChange?(option: ISelectOption, id: string): void
  disabled?: boolean
  invalid?: boolean
  validationMessage?: string
  large?: boolean
  required?: boolean
  className?: string
  ariaLabel?: string
  renderOptionType?: SelectOptionRenderType
}

/** @deprecated */
export const Select = memo<ISelectProps>(
  ({
    id = uuid(),
    label: selectLabel,
    placeholder = 'Select one',
    dataTest = `select-${id}`,
    value,
    options,
    onChange,
    disabled,
    required,
    invalid,
    validationMessage,
    large,
    className,
    ariaLabel,
  }) => {
    const selectedOption = useMemo(() => {
      if (typeof value === 'string' || typeof value === 'number') {
        return options.find(o => o.value === value)
      }
      return value
    }, [options, value])

    const optionSelectHandler = (option: ISelectOption) => onChange && onChange(option, id)

    return (
      <SelectBase
        ariaLabel={ariaLabel}
        id={id}
        label={selectLabel}
        dataTest={dataTest}
        displayValue={
          <DisplayOptionLabel label={selectedOption?.label} placeholder={placeholder} />
        }
        hideOnSelect
        disabled={disabled}
        required={required}
        invalid={invalid}
        validationMessage={validationMessage}
        large={large}
        className={className}
        selectButtonType='select-display-value'>
        {options.map(({ className: classNameProp, label: optionLabel, value: optionValue }) => (
          <SelectOption
            key={`select-${id}-option-${optionValue}`}
            label={optionLabel}
            value={optionValue}
            selected={optionValue === selectedOption?.value}
            onSelect={optionSelectHandler}
            dataTest={`${dataTest}-${optionValue}`}
            className={classNameProp}
          />
        ))}
      </SelectBase>
    )
  }
)
